import * as THREE from 'three/webgpu';
import { isSketchContour } from '../meshProxy/SketchContour';

/**
 * 可清理上色草图轮廓的草图对象协议。
 */
export interface SketchContourDisplayOwner extends THREE.Object3D {
    /** 清理当前对象持有的上色草图轮廓显示。 */
    clearContourDisplay: () => void;
}

/**
 * 判断对象是否具备上色草图轮廓清理能力。
 *
 * @param value 待判断对象。
 * @returns 是否为轮廓显示持有者。
 */
function isSketchContourDisplayOwner(value: THREE.Object3D): value is SketchContourDisplayOwner {
    return typeof Reflect.get(value, 'clearContourDisplay') === 'function';
}

/**
 * 释放轮廓网格资源。
 *
 * @param contour 轮廓网格。
 */
export function disposeSketchContour(contour: THREE.Mesh): void {
    contour.geometry.dispose();
    const material = contour.material;
    if (Array.isArray(material)) {
        material.forEach((item) => item.dispose());
        return;
    }
    material.dispose();
}

/**
 * 清理对象树内全部上色草图轮廓显示。
 *
 * 设计说明：
 * 1. “上色草图轮廓”只属于草图绘制/编辑生命周期，草图结束后不应继续作为场景面存在；
 * 2. 轮廓面与后续拉伸起始面共面时会抢占鼠标命中，因此必须在 cad-core 生命周期出口统一移除；
 * 3. 这里只移除显示代理，不修改草图线、圆、槽口等真实几何。
 *
 * @param root 需要清理的对象树根节点。
 */
export function clearSketchContourDisplays(root: THREE.Object3D): void {
    const owners: SketchContourDisplayOwner[] = [];
    const contours: THREE.Mesh[] = [];

    root.traverse((object) => {
        if (isSketchContourDisplayOwner(object)) {
            owners.push(object);
        }
        if (isSketchContour(object)) {
            contours.push(object);
        }
    });

    owners.forEach((owner) => {
        owner.clearContourDisplay();
    });

    contours.forEach((contour) => {
        if (!contour.parent) {
            return;
        }
        contour.parent.remove(contour);
        disposeSketchContour(contour);
    });
}
